export type ImageModelId =
	| "nano-banana-2"
	| "recraft-v4"
	| "recraft-v4-pro"
	| "gpt-image-2";

export type ImageDimension = {
	height: number;
	label: string;
	width: number;
};

export type ImageModelDefinition = {
	defaultHeight: number;
	defaultWidth: number;
	description: string;
	id: ImageModelId;
	isDefault: boolean;
	label: string;
	pricing: {
		type: "fixed-by-dimensions";
		usdByDimensions: Record<string, number>;
	};
	providerModelId: string;
	providerName: string;
	supportedDimensions: ImageDimension[];
};

const NANO_BANANA_2_DIMENSIONS = [
	{ width: 1024, height: 1024, label: "1K (1:1)" },
	{ width: 2048, height: 2048, label: "2K (1:1)" },
	{ width: 1200, height: 896, label: "1K (4:3)" },
	{ width: 2400, height: 1792, label: "2K (4:3)" },
	{ width: 896, height: 1200, label: "1K (3:4)" },
	{ width: 1792, height: 2400, label: "2K (3:4)" },
	{ width: 1376, height: 768, label: "1K (16:9)" },
	{ width: 2752, height: 1536, label: "2K (16:9)" },
	{ width: 768, height: 1376, label: "1K (9:16)" },
	{ width: 1536, height: 2752, label: "2K (9:16)" },
] satisfies ImageDimension[];

const RECRAFT_V4_DIMENSIONS = [
	{ width: 1024, height: 1024, label: "1K (1:1)" },
	{ width: 1216, height: 896, label: "1K (4:3)" },
	{ width: 896, height: 1216, label: "1K (3:4)" },
	{ width: 1344, height: 768, label: "1K (16:9)" },
	{ width: 768, height: 1344, label: "1K (9:16)" },
] satisfies ImageDimension[];

const RECRAFT_V4_PRO_DIMENSIONS = [
	{ width: 2048, height: 2048, label: "2K (1:1)" },
	{ width: 2432, height: 1792, label: "2K (4:3)" },
	{ width: 1792, height: 2432, label: "2K (3:4)" },
	{ width: 2688, height: 1536, label: "2K (16:9)" },
	{ width: 1536, height: 2688, label: "2K (9:16)" },
] satisfies ImageDimension[];

const GPT_IMAGE_2_DIMENSIONS = [
	{ width: 1024, height: 1024, label: "1K (1:1)" },
	{ width: 2048, height: 2048, label: "2K (1:1)" },
	{ width: 1024, height: 768, label: "1K (4:3)" },
	{ width: 2048, height: 1536, label: "2K (4:3)" },
	{ width: 768, height: 1024, label: "1K (3:4)" },
	{ width: 1536, height: 2048, label: "2K (3:4)" },
	{ width: 1280, height: 720, label: "1K (16:9)" },
	{ width: 2560, height: 1440, label: "2K (16:9)" },
	{ width: 720, height: 1280, label: "1K (9:16)" },
	{ width: 1440, height: 2560, label: "2K (9:16)" },
] satisfies ImageDimension[];

function fixedPricing(dimensions: ImageDimension[], usd: number) {
	return Object.fromEntries(
		dimensions.map((dimension) => [
			`${dimension.width}x${dimension.height}`,
			usd,
		]),
	);
}

function splitPricing(
	dimensions: ImageDimension[],
	oneKUsd: number,
	twoKUsd: number,
) {
	return Object.fromEntries(
		dimensions.map((dimension) => [
			`${dimension.width}x${dimension.height}`,
			dimension.label.startsWith("2K") ? twoKUsd : oneKUsd,
		]),
	);
}

export const IMAGE_MODELS: ImageModelDefinition[] = [
	{
		defaultHeight: 1024,
		defaultWidth: 1024,
		description:
			"Google's flagship generation model for detailed, instruction-following visual creation.",
		id: "nano-banana-2",
		isDefault: true,
		label: "Nano Banana 2",
		pricing: {
			type: "fixed-by-dimensions",
			usdByDimensions: fixedPricing(NANO_BANANA_2_DIMENSIONS, 0.138),
		},
		providerModelId: "google:4@3",
		providerName: "Google",
		supportedDimensions: NANO_BANANA_2_DIMENSIONS,
	},
	{
		defaultHeight: 1024,
		defaultWidth: 1024,
		description: "Professional-grade model for design and marketing workflows.",
		id: "recraft-v4",
		isDefault: false,
		label: "Recraft V4",
		pricing: {
			type: "fixed-by-dimensions",
			usdByDimensions: fixedPricing(RECRAFT_V4_DIMENSIONS, 0.04),
		},
		providerModelId: "recraft:v4@0",
		providerName: "Recraft",
		supportedDimensions: RECRAFT_V4_DIMENSIONS,
	},
	{
		defaultHeight: 2048,
		defaultWidth: 2048,
		description: "Advanced 2K model for brand-critical creative production.",
		id: "recraft-v4-pro",
		isDefault: false,
		label: "Recraft V4 Pro",
		pricing: {
			type: "fixed-by-dimensions",
			usdByDimensions: fixedPricing(RECRAFT_V4_PRO_DIMENSIONS, 0.28),
		},
		providerModelId: "recraft:v4-pro@0",
		providerName: "Recraft",
		supportedDimensions: RECRAFT_V4_PRO_DIMENSIONS,
	},
	{
		defaultHeight: 2048,
		defaultWidth: 2048,
		description: "OpenAI's latest image model via Runware.",
		id: "gpt-image-2",
		isDefault: false,
		label: "GPT Image 2",
		pricing: {
			type: "fixed-by-dimensions",
			usdByDimensions: splitPricing(GPT_IMAGE_2_DIMENSIONS, 0.12, 0.22),
		},
		providerModelId: "openai:gpt-image@2",
		providerName: "OpenAI",
		supportedDimensions: GPT_IMAGE_2_DIMENSIONS,
	},
];

export function getDefaultImageModel() {
	return IMAGE_MODELS.find((model) => model.isDefault) ?? IMAGE_MODELS[0];
}

export function getImageModel(modelId?: string | null) {
	if (!modelId) return getDefaultImageModel();
	return IMAGE_MODELS.find((model) => model.id === modelId) ?? null;
}

export function getImageModelOrThrow(modelId?: string | null) {
	const model = getImageModel(modelId);
	if (!model) {
		throw new Error("Unsupported image model.");
	}
	return model;
}

export function getImageDimensionOrThrow(
	model: ImageModelDefinition,
	width: number,
	height: number,
) {
	const key = `${width}x${height}`;
	const dimension = model.supportedDimensions.find(
		(entry) => `${entry.width}x${entry.height}` === key,
	);
	if (!dimension) {
		const supported = model.supportedDimensions
			.map((entry) => `${entry.width}x${entry.height}`)
			.join(", ");
		throw new Error(
			`${model.label} currently supports these image sizes: ${supported}.`,
		);
	}
	return dimension;
}

export function listImageModels() {
	return IMAGE_MODELS.map((model) => ({
		defaultHeight: model.defaultHeight,
		defaultWidth: model.defaultWidth,
		description: model.description,
		id: model.id,
		isDefault: model.isDefault,
		label: model.label,
		pricing: model.pricing,
		provider: model.providerName,
		providerModelId: model.providerModelId,
		supportedDimensions: model.supportedDimensions,
		type: "image" as const,
	}));
}
